import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, MessageCircle, CalendarCheck, Video, Lock, IndianRupee,
  HeartHandshake, Sprout, ShieldCheck, Check, Wind, Flame, Compass,
  CloudRain, Sparkles, Star,
} from "lucide-react";
import { THERAPISTS } from "@/data/therapists";
import { Reveal, MaskedLine } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";
import TherapistCard from "@/components/TherapistCard";

const RECOGNITION = [
  "You're functioning. But you're exhausted.",
  "You've become really good at saying \"I'm fine.\"",
  "You keep replaying conversations long after they're over.",
  "You have people around you, but still feel alone.",
  "You smile through the day, and fall apart at night.",
  "No matter how hard you try, it never feels like enough.",
  "You know something needs to change. You just don't know where to start.",
];

const CHAPTERS = [
  {
    n: "01",
    icon: MessageCircle,
    title: "Message",
    text: "Send us a WhatsApp message. No forms, no phone calls, no explaining yourself to a stranger at a front desk.",
  },
  {
    n: "02",
    icon: CalendarCheck,
    title: "Book",
    text: "Choose your therapist and a time that works for you. Your first session starts at ₹99 — honest pricing, no games.",
  },
  {
    n: "03",
    icon: Video,
    title: "Talk",
    text: "A secure, confidential video session. 30 to 60 minutes that belong entirely to you.",
  },
];

const WHY = [
  {
    icon: Lock,
    chip: "bg-[#E9F0E2] text-[#4F6440]",
    span: "md:col-span-7",
    title: "Truly private",
    text: "We don't store your chats. No records, no history, no paper trail. Each session is a clean slate — just you and your psychologist.",
  },
  {
    icon: IndianRupee,
    chip: "bg-[#E4EDDA] text-[#56704A]",
    span: "md:col-span-5",
    title: "Honest pricing",
    text: "Your first session is ₹99 — the same honest price for every therapist. No fake discounts, no games.",
  },
  {
    icon: HeartHandshake,
    chip: "bg-[#ECF1E4] text-[#41563A]",
    span: "md:col-span-5",
    title: "Judgment-free",
    text: "First session or fiftieth — you get empathy, not stigma. Nothing you bring is too small, too strange, or too much.",
  },
  {
    icon: Sprout,
    chip: "bg-[#DFE9D3] text-[#5F7A50]",
    span: "md:col-span-7",
    title: "Made for first-timers",
    text: "Never spoken to a therapist? Most people who come here haven't either. You don't need to prepare anything or find the right words — your therapist will gently guide the conversation.",
  },
];

const STATS = [
  { value: "₹99", title: "Your first session — therapy that doesn't wait for payday" },
  { value: "100%", title: "Private. No chat records, no history, no paper trail" },
  { value: "6", title: "Languages to choose from — talk in the one that feels like home" },
  { value: "0", title: "Judgment. First session or fiftieth, you're met with empathy" },
];

const SUPPORT = [
  { id: "anxiety", icon: Wind, chip: "bg-[#E9F0E2] text-[#4F6440]", title: "Anxiety & overthinking", text: "Racing thoughts, worry loops, restlessness that won't switch off." },
  { id: "stress", icon: Flame, chip: "bg-[#E4EDDA] text-[#56704A]", title: "Stress & burnout", text: "Work, studies, or just running on empty for too long." },
  { id: "relationships", icon: HeartHandshake, chip: "bg-[#ECF1E4] text-[#41563A]", title: "Relationships", text: "Partners, family, friendships — patterns that keep repeating." },
  { id: "self-esteem", icon: Sparkles, chip: "bg-[#DFE9D3] text-[#5F7A50]", title: "Self-worth & identity", text: "Self-doubt, image, the feeling of never being enough." },
  { id: "trauma", icon: CloudRain, chip: "bg-[#E8EFE6] text-[#3E5C46]", title: "Grief & difficult experiences", text: "Loss, or something from the past that still weighs on you." },
  { id: "unsure", icon: Compass, chip: "bg-[#E2ECDD] text-[#4C6B3F]", title: "Not sure yet", text: "Something feels off but you can't name it. That's a fine place to start." },
];

const TESTIMONIALS = [
  {
    quote: "I finally felt like someone truly listened, without judgment. Eunoia gave me a voice again.",
    by: "Female, 27, Bengaluru, Marketing Manager",
  },
  {
    quote: "The sessions helped me navigate a difficult period with a sense of calm I hadn't felt in years.",
    by: "Male, 34, Mumbai, Software Engineer",
  },
  {
    quote: "Booking took two minutes and there was no awkward small talk — just got straight to what mattered.",
    by: "Female, 24, Pune, Graduate Student",
  },
  {
    quote: "I'd put off therapy for years assuming it would be expensive or complicated. This wasn't either.",
    by: "Male, 41, Delhi, Small Business Owner",
  },
];

export default function Home() {
  const therapists = THERAPISTS;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-[-8%] h-[36rem] w-[36rem] rounded-full bg-[#DCE7CF] opacity-50 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-[-25%] left-[-8%] h-[32rem] w-[32rem] rounded-full bg-[#E6EEDC] opacity-60 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute left-[38%] top-[35%] h-[26rem] w-[26rem] rounded-full bg-[#EFF3E6] opacity-60 blur-3xl" />
        <div aria-hidden="true" className="stripes absolute inset-0" />

        <div className="relative mx-auto grid max-w-screen-2xl gap-14 px-6 pb-28 pt-14 md:px-12 md:pt-20 lg:grid-cols-12 lg:gap-8 lg:px-24">
          <motion.div style={{ y: textY }} className="relative flex flex-col justify-center lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="inline-flex w-fit items-center gap-2.5 rounded-full bg-secondary px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.22em] text-foreground/70"
              data-testid="hero-eyebrow"
            >
              <span className="h-2 w-2 rounded-full bg-accent" /> Therapy in India, done differently
            </motion.span>

            <h1 className="mt-8 font-serif text-[clamp(2.9rem,7.2vw,6.75rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.03em] text-balance" data-testid="hero-headline">
              <MaskedLine delay={0.35}>It's okay to</MaskedLine>
              <MaskedLine delay={0.5}>
                <span className="text-accent">not be okay.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
              data-testid="hero-subcopy"
            >
              Eunoia is a quiet place to talk, to be heard, and to find your way back to yourself.
              Professional therapy that's private, affordable, and free of judgment — starting with
              one simple conversation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/find"
                data-testid="hero-cta-find-therapist"
                className="group flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-bold text-primary-foreground transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Find a therapist
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-how-it-works"
                className="rounded-full bg-secondary px-7 py-4 text-sm font-bold transition-all duration-300 hover:bg-accent hover:text-accent-foreground"
              >
                How therapy works
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="mt-6 flex items-center gap-2 text-xs font-semibold text-muted-foreground"
              data-testid="hero-trust-row"
            >
              <Lock className="h-3.5 w-3.5 text-accent" /> Starts at just ₹99 · 30 to 60 minutes · private & safe
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="mt-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"
              data-testid="hero-social-proof-row"
            >
              <Star className="h-3.5 w-3.5 fill-accent text-accent" /> 50+ sessions held · 4.9/5 average rating
            </motion.p>
          </motion.div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative ml-auto w-full max-w-md"
            >
              <div data-testid="hero-illustration">
                <BotanicalArt variant="tangle" caption="A tangled mind, gently untangled." className="rounded-[2.5rem]" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS TILES */}
      <section className="mx-auto max-w-screen-2xl px-6 pb-6 md:px-12 lg:px-24" data-testid="stats-section">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.value} delay={i * 0.07}>
              <div className="tile h-full p-7" data-testid={`stat-tile-${i}`}>
                <p className="font-serif text-4xl font-bold tracking-tight text-accent">{s.value}</p>
                <p className="mt-3 text-sm font-semibold leading-relaxed text-muted-foreground">{s.title}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RECOGNITION */}
      <section className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24" data-testid="recognition-section">
        <Reveal>
          <p className="eyebrow">Read these slowly</p>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            Does any of this feel a little too familiar?
          </h2>
          <p
            className="mt-6 flex flex-wrap gap-x-2 gap-y-1 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground md:tracking-[0.25em]"
            data-testid="recognition-tags"
          >
            {["Stress", "Anxiety", "Overthinking", "Relationships", "Feeling stuck"].map((tag, i, arr) => (
              <span key={tag}>
                {tag}
                {i < arr.length - 1 && <span className="ml-2 text-muted-foreground/50">·</span>}
              </span>
            ))}
          </p>
        </Reveal>

        <div className="mt-14">
          {RECOGNITION.map((line, i) => (
            <Reveal key={line} delay={i * 0.05}>
              <div
                className="group flex items-baseline gap-6 border-t border-border py-6 transition-all duration-500 hover:pl-4 md:py-8"
                data-testid={`recognition-line-${i}`}
              >
                <span className="font-serif text-sm italic text-accent md:text-base">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-2xl font-medium leading-snug tracking-tight transition-colors duration-500 group-hover:text-accent md:text-4xl">
                  {line}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            If any of these landed — that's not weakness, and it's not "all in your head."
            It's a signal. And you don't have to figure out what to do with it alone.
          </p>
        </Reveal>
      </section>

      {/* AREAS OF SUPPORT — tiles */}
      <section className="border-y border-border bg-surface/60" data-testid="support-section">
        <div className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24">
          <Reveal className="text-center">
            <p className="eyebrow">Areas of support</p>
            <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              You can bring whatever is on your mind.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Pick what feels closest — we'll match you with someone who works with it every day.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORT.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07}>
                <Link
                  to={`/find?concern=${s.id}`}
                  data-testid={`support-${s.id}`}
                  className="tile group flex h-full flex-col items-center p-8 text-center transition-transform duration-500 hover:-translate-y-1.5"
                >
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${s.chip}`}>
                    <s.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-primary/20 px-5 py-2 text-xs font-bold transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    Find support <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TURNING POINT — dark chapter */}
      <section className="bg-gradient-to-br from-[#12573E] via-[#0C3B2E] to-[#07271C] text-white" data-testid="turning-point-section">
        <div className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-36 lg:px-24">
          <Reveal>
            <p className="font-serif text-4xl font-extrabold leading-[1.05] tracking-tight text-balance md:text-6xl lg:text-7xl">
              But you don't have to feel this way{" "}
              <span className="italic text-white/60">alone.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
              Not tonight. Not by white-knuckling through another week. What if talking to
              someone — someone trained, kind, and bound to confidentiality — was this simple?
            </p>
          </Reveal>
        </div>
      </section>

      {/* CHAPTERS — how it works */}
      <section className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24" data-testid="chapters-section">
        <Reveal className="text-center">
          <p className="eyebrow">A path to feeling better</p>
          <h2 className="mt-6 font-serif text-4xl font-bold tracking-tight md:text-5xl">
            Three small steps. That's all.
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12}>
              <div className="tile group h-full p-10 text-center transition-transform duration-500 hover:-translate-y-1.5" data-testid={`chapter-${c.n}`}>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary font-serif text-base font-bold text-primary-foreground transition-all duration-500 group-hover:scale-110 group-hover:bg-accent">
                  {c.n}
                </span>
                <c.icon className="mx-auto mt-6 h-6 w-6 text-accent" />
                <h3 className="mt-5 font-serif text-3xl font-bold tracking-tight">{c.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <Link
            to="/how-it-works"
            data-testid="chapters-cta"
            className="group mt-14 inline-flex items-center gap-2 text-sm font-bold text-accent"
          >
            See exactly what a first session looks like
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* WHY EUNOIA — tile panel */}
      <section data-testid="why-section">
        <div className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24">
          <Reveal>
            <div className="tile p-8 md:p-14">
              <div className="max-w-2xl">
                <p className="eyebrow">Why Eunoia</p>
                <h2 className="mt-6 font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
                  Built for the things you're quietly worried about.
                </h2>
              </div>
              <div className="mt-12 grid gap-5 sm:grid-cols-2">
                {WHY.map((w) => (
                  <div
                    key={w.title}
                    className="rounded-2xl border border-border bg-muted/60 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-accent/40"
                    data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${w.chip}`}>
                      <w.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 font-serif text-2xl font-bold tracking-tight">{w.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{w.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THERAPISTS */}
      <section className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24" data-testid="therapists-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Your compassionate guides</p>
              <h2 className="mt-6 max-w-2xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
                People you'll actually feel comfortable talking to.
              </h2>
            </div>
            <Link
              to="/find"
              data-testid="therapists-cta-find"
              className="group flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Find your match <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div
          className="mt-14 grid justify-center gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 260px))" }}
        >
          {therapists.map((t, i) => (
            <TherapistCard key={t.id} therapist={t} index={i} />
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="border-y border-border bg-surface" data-testid="pricing-section">
        <div className="mx-auto grid max-w-screen-2xl items-center gap-14 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-2 lg:px-24">
          <Reveal>
            <p className="eyebrow">Investing in your peace of mind</p>
            <h2 className="mt-6 font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              One honest price.<br />No games.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              Professional mental healthcare shouldn't feel financially out of reach.
              So we made the price simple enough that you never have to think about it twice.
            </p>
            <Link
              to="/pricing"
              data-testid="pricing-cta"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-accent"
            >
              Everything about pricing & cancellations
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-primary/10 bg-background p-10 md:p-12" data-testid="pricing-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Start your therapy at</p>
              <p className="mt-4 font-serif text-7xl font-extrabold tracking-tight text-accent">
                ₹99
              </p>
              <p className="mt-2 font-serif text-lg italic text-accent">Your first session</p>
              <p className="mt-8 text-sm leading-relaxed text-muted-foreground md:text-base">
                Same ₹99 price for every therapist — no hidden tiers, no surprises.
              </p>
              <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="h-4 w-4 shrink-0 text-accent" />
                Always confidential · free rescheduling over WhatsApp
              </p>
              <Link
                to="/find"
                data-testid="pricing-card-cta"
                className="mt-10 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-bold text-primary-foreground transition-transform duration-300 hover:scale-[1.02] active:scale-95"
              >
                Take the first step <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-screen-2xl px-6 py-24 md:px-12 md:py-32 lg:px-24" data-testid="testimonials-section">
        <Reveal>
          <h2 className="max-w-2xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            Quiet moments of clarity, in their words.
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">Shared with permission. Always anonymous.</p>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.by + i} delay={i * 0.1}>
              <figure className="h-full rounded-2xl border border-primary/10 bg-card p-10" data-testid={`testimonial-${i}`}>
                <blockquote className="font-serif text-2xl font-medium italic leading-relaxed tracking-tight md:text-3xl">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  — {t.by}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRIVACY STRIP */}
      <section className="border-t border-border" data-testid="privacy-strip">
        <div className="mx-auto max-w-screen-2xl px-6 py-20 md:px-12 md:py-24 lg:px-24">
          <Reveal>
            <p className="text-center font-serif text-3xl font-bold tracking-tight md:text-5xl">
              No diagnosis. <span className="text-accent">No judgment.</span> No records kept.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary text-primary-foreground" data-testid="final-cta-section">
        <div className="mx-auto max-w-screen-2xl px-6 py-28 md:px-12 md:py-40 lg:px-24">
          <Reveal>
            <p className="max-w-4xl font-serif text-4xl font-bold leading-tight tracking-tight text-balance md:text-6xl">
              You don't have to figure everything out today.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-xl font-serif text-2xl italic text-primary-foreground/70 md:text-3xl">
              You can just start with one conversation.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/find"
                data-testid="final-cta-find"
                className="group flex items-center gap-2 rounded-full bg-primary-foreground px-8 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Find someone to talk to
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="final-cta-contact"
                className="rounded-full border border-primary-foreground/40 px-8 py-4 text-sm font-bold transition-colors duration-300 hover:bg-primary-foreground hover:text-primary"
              >
                Talk to Eunoia first
              </Link>
            </div>
            <p className="mt-6 flex items-center gap-2 text-xs font-semibold text-primary-foreground/60" data-testid="final-cta-microcopy">
              <Lock className="h-3.5 w-3.5" /> One conversation from ₹99 · No subscription · Private & safe
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
